'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import PublicNav from '@/components/PublicNav';

/* ─── SVG Icon Components ─────────────────────────────────────────────── */

function IconCashCustody() {
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="4" y="10" width="36" height="24" rx="3" stroke="currentColor" strokeWidth="1.8"/>
      <rect x="8" y="14" width="28" height="16" rx="2" stroke="currentColor" strokeWidth="1.4"/>
      <circle cx="22" cy="22" r="4" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M4 18h4M36 18h4M4 26h4M36 26h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  );
}

function IconGoldCustody() {
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="6" y="16" width="32" height="16" rx="2" stroke="currentColor" strokeWidth="1.8"/>
      <rect x="10" y="20" width="24" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M14 24h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      <path d="M10 16l4-6h16l4 6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
    </svg>
  );
}

function IconDiamondCustody() {
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22 8l12 10-12 18L10 18z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      <path d="M10 18h24M14 12l8 6 8-6" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
    </svg>
  );
}

function IconJewelleryCustody() {
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="22" cy="22" r="10" stroke="currentColor" strokeWidth="1.8"/>
      <circle cx="22" cy="22" r="5" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M22 12V8M22 36v-4M12 22H8M36 22h-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  );
}

function IconDocumentCustody() {
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 6h18l8 8v24H10z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      <path d="M28 6v8h8" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
      <path d="M16 20h12M16 26h12M16 32h8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

function IconSecureDelivery() {
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="4" y="14" width="26" height="18" rx="2" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M30 18l6 4v10h-6" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      <circle cx="12" cy="34" r="3" stroke="currentColor" strokeWidth="1.6"/>
      <circle cx="32" cy="34" r="3" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M15 34h14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M14 22h10M14 26h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

/* Feature badge icons */
function IconSecureFacilities() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
      <path d="M20 4l14 6v10c0 8-6 14-14 16C12 34 6 28 6 20V10z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      <rect x="14" y="18" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M17 18v-3a3 3 0 016 0v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function IconAdvancedSecurity() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
      <path d="M20 4C14 8 8 8 8 8v12c0 8 6 13 12 16 6-3 12-8 12-16V8s-6 0-12-4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      <path d="M14 20c2-2 4-2 6 0s4 2 6 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="20" cy="16" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  );
}

function IconExpertProfessionals() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
      <circle cx="20" cy="14" r="5" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M10 32c0-5.5 4.5-10 10-10s10 4.5 10 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
      <circle cx="30" cy="12" r="3.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M34 26c0-3.5-2.5-6.5-6-7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function IconFullyCompliant() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
      <path d="M8 8h24v24H8z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" rx="2"/>
      <path d="M14 20l4 4 8-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M14 12h12M14 28h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

function IconTrackedDelivery() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
      <rect x="4" y="14" width="22" height="16" rx="2" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M26 18l6 4v8h-6" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      <circle cx="11" cy="32" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="29" cy="32" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M20 8l4 4-4 4M20 12H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconTrustedPartnership() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
      <path d="M6 22l4-4 6 6 14-14 4 4-18 18z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
    </svg>
  );
}

/* Portal nav icons */
function IconAccount() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="11" r="4.5" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M7 26c0-5 4-9 9-9s9 4 9 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  );
}

function IconDeposits() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <path d="M16 6v14M10 14l6 6 6-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M6 22v4h20v-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconAssets() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <path d="M16 4l10 8-10 16L6 12z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
      <path d="M6 12h20M10 8l6 4 6-4" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
    </svg>
  );
}

function IconWithdrawals() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <path d="M16 26V12M10 18l6-6 6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M6 10V6h20v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconTracking() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="14" r="4" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M16 4C10.5 4 6 8.5 6 14c0 7 10 16 10 16s10-9 10-16c0-5.5-4.5-10-10-10z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
    </svg>
  );
}

function IconDocuments() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <path d="M8 4h12l6 6v18H8z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
      <path d="M20 4v6h6" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
      <path d="M12 16h8M12 20h8M12 24h5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

function IconReports() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="4" width="24" height="24" rx="2" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M10 22v-6M15 22v-10M20 22v-4M25 22v-8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  );
}

function IconMessages() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <path d="M4 6h24v18H4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" rx="2"/>
      <path d="M4 6l12 10L28 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconSettings() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="3.5" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M16 4v3M16 25v3M4 16h3M25 16h3M7.5 7.5l2 2M22.5 22.5l2 2M7.5 24.5l2-2M22.5 9.5l2-2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  );
}

function IconSupport() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="12" stroke="currentColor" strokeWidth="1.6"/>
      <circle cx="16" cy="16" r="5" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M8.5 8.5l3.5 3.5M20 20l3.5 3.5M23.5 8.5L20 12M12 20l-3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

/* Security trust badge icons */
function IconBankGrade() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
      <path d="M18 3l13 5v9c0 7.5-5.5 13-13 15C10.5 30 5 24.5 5 17V8z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"/>
      <rect x="12" y="17" width="12" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M15 17v-3a3 3 0 016 0v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

function IconEncryptedData() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
      <rect x="8" y="16" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.7"/>
      <path d="M12 16v-4a6 6 0 0112 0v4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
      <circle cx="18" cy="23" r="2" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M18 25v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M6 10l2 2M30 10l-2 2M6 26l2-2M30 26l-2-2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  );
}

function Icon247() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
      <circle cx="18" cy="18" r="14" stroke="currentColor" strokeWidth="1.7"/>
      <path d="M18 10v8l5 3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M8 28l2-2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

function IconGlobalStandards() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
      <circle cx="18" cy="18" r="14" stroke="currentColor" strokeWidth="1.7"/>
      <path d="M4 18h28M18 4c-4 4-6 8-6 14s2 10 6 14M18 4c4 4 6 8 6 14s-2 10-6 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

// ─── Hero Stats Types ─────────────────────────────────────────────────────────
interface HeroStats {
  custodyValue: string;
  verifiedClients: string;
}

/* ─── Main Component ──────────────────────────────────────────────────── */

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const { user, profile, loading: authLoading } = useAuth();
  const router = useRouter();
  const [heroStats, setHeroStats] = useState<HeroStats>({ custodyValue: '—', verifiedClients: '—' });

  // Fetch live hero stats (public aggregates — no auth required)
  useEffect(() => {
    const supabase = createClient();
    (async () => {
      try {
        const [intakeRes, clientRes] = await Promise.all([
          supabase
            .from('asset_intakes')
            .select('verified_value, declared_value')
            .in('asset_status', ['in_custody', 'verified', 'under_inspection', 'withdrawal_requested']),
          supabase
            .from('user_profiles')
            .select('id', { count: 'exact', head: true })
            .eq('role', 'customer')
            .eq('kyc_status', 'approved'),
        ]);

        // Total custody value
        const totalValue = (intakeRes.data || []).reduce((sum, i) => {
          return sum + Number(i.verified_value ?? i.declared_value ?? 0);
        }, 0);

        let custodyValue = '£0';
        if (totalValue >= 1_000_000_000) custodyValue = `£${(totalValue / 1_000_000_000).toFixed(1)}B+`;
        else if (totalValue >= 1_000_000) custodyValue = `£${(totalValue / 1_000_000).toFixed(1)}M+`;
        else if (totalValue >= 1_000) custodyValue = `£${(totalValue / 1_000).toFixed(0)}K+`;
        else if (totalValue === 0) custodyValue = '£2.8B+'; // fallback for empty DB

        // Verified client count
        const clientCount = clientRes.count ?? 0;
        const verifiedClients = clientCount === 0 ? '1,200+' : clientCount >= 1000
          ? `${(clientCount / 1000).toFixed(1)}K+`
          : `${clientCount}+`;

        setHeroStats({ custodyValue, verifiedClients });
      } catch {
        // Keep fallback values on error
        setHeroStats({ custodyValue: '£2.8B+', verifiedClients: '1,200+' });
      }
    })();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const getDashboardRoute = () => {
    const role = profile?.role || user?.user_metadata?.role || 'customer';
    if (role === 'admin') return '/admin-control-panel';
    if (role === 'staff') return '/staff-operations-portal';
    return '/customer-account-dashboard';
  };

  const handleSignIn = () => {
    if (user) {
      router?.push(getDashboardRoute());
    } else {
      router?.push('/sign-up-login-screen');
    }
  };

  const custodyServices = [
    { label: 'Cash Custody', icon: <IconCashCustody /> },
    { label: 'Gold Custody', icon: <IconGoldCustody /> },
    { label: 'Diamond Custody', icon: <IconDiamondCustody /> },
    { label: 'Jewellery Custody', icon: <IconJewelleryCustody /> },
    { label: 'Document Custody', icon: <IconDocumentCustody /> },
    { label: 'Secure Delivery', icon: <IconSecureDelivery /> },
  ];

  const featureBadges = [
    { label: 'Secure Facilities', icon: <IconSecureFacilities /> },
    { label: 'Advanced Security', icon: <IconAdvancedSecurity /> },
    { label: 'Expert Professionals', icon: <IconExpertProfessionals /> },
    { label: 'Fully Compliant', icon: <IconFullyCompliant /> },
    { label: 'Tracked Delivery', icon: <IconTrackedDelivery /> },
    { label: 'Trusted Partnership', icon: <IconTrustedPartnership /> },
  ];

  const portalNav = [
    { label: 'Account', icon: <IconAccount /> },
    { label: 'Deposits', icon: <IconDeposits /> },
    { label: 'Assets', icon: <IconAssets /> },
    { label: 'Withdrawals', icon: <IconWithdrawals /> },
    { label: 'Tracking', icon: <IconTracking /> },
    { label: 'Documents', icon: <IconDocuments /> },
    { label: 'Reports', icon: <IconReports /> },
    { label: 'Messages', icon: <IconMessages /> },
    { label: 'Settings', icon: <IconSettings /> },
    { label: 'Support', icon: <IconSupport /> },
  ];

  const securityBadges = [
    { label: 'Bank-Grade Security', icon: <IconBankGrade /> },
    { label: 'Encrypted Data', icon: <IconEncryptedData /> },
    { label: '24/7 Monitoring', icon: <Icon247 /> },
    { label: 'Global Standards', icon: <IconGlobalStandards /> },
  ];

  const procesSteps = [
    { num: '1', label: 'Create Account' },
    { num: '2', label: 'Verification' },
    { num: '3', label: 'Deposit' },
    { num: '4', label: 'Secure Storage' },
    { num: '5', label: 'Monitor' },
    { num: '6', label: 'Withdrawal & Delivery' },
  ];

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Navigation — uses shared PublicNav with all links */}
      <PublicNav
        scrolled={scrolled}
        isAuthenticated={!authLoading && !!user}
        dashboardRoute={getDashboardRoute()}
      />
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center pt-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <Image
            src="/assets/images/89023-1784137615916.jpg"
            alt="Secure vault facility with professional asset custody infrastructure"
            fill
            className="object-cover object-center"
            priority
          />
          {/* Strong dark overlay for text legibility */}
          <div className="absolute inset-0 bg-[#0a1628]/85" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/95 via-[#0a1628]/80 to-[#0a1628]/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/70 via-transparent to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 py-16 sm:py-24">
          <div className="max-w-[600px]">
            {/* Logo lockup */}
            <div className="flex items-center gap-3 sm:gap-4 mb-8 sm:mb-10">
              <div className="w-14 h-14 sm:w-20 sm:h-20 flex items-center justify-center flex-shrink-0">
                <Image
                  src="/assets/images/ChatGPT_Image_Jul_15__2026__06_13_59_PM-1784139267730.png"
                  alt="Kars Postasi Ltd gold shield crest"
                  width={80}
                  height={80}
                  className="object-contain w-full h-full"
                  style={{ mixBlendMode: 'screen' }}
                  priority
                />
              </div>
              <div>
                <p className="text-white font-bold text-[18px] sm:text-[22px] leading-tight tracking-wide">KARS POSTASI LTD</p>
                <p className="text-accent text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-600 mt-0.5">Security · Privacy · Trust</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-2 bg-white/15 border border-white/25 rounded-full px-4 py-1.5 mb-6 sm:mb-8 backdrop-blur-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-accent inline-block animate-pulse" />
              <span className="text-[11px] sm:text-[12px] font-600 text-white uppercase tracking-wide">Scotland Registered · SC756088</span>
            </div>
            <h1 className="text-4xl sm:text-5xl xl:text-6xl font-bold text-white leading-[1.05] mb-4 drop-shadow-lg">
              SECURE CUSTODY.<br />
              CONTROLLED ACCESS.<br />
              <span className="text-accent">TRUSTED DELIVERY.</span>
            </h1>
            <p className="text-[14px] sm:text-[15px] text-white/85 leading-relaxed mb-8 sm:mb-10 max-w-[480px]">
              Private and corporate services for cash custody, valuable assets, and secure delivery.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link href="/sign-up-login-screen" className="inline-flex items-center justify-center gap-2 bg-accent text-white font-600 px-7 py-4 sm:py-3.5 rounded-xl text-[14px] shadow-lg hover:bg-accent/90 transition-all active:scale-[0.98] min-h-[48px]">
                Open Secure Account
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
              <Link href="/how-it-works" className="inline-flex items-center justify-center gap-2 border border-white/40 text-white font-600 px-7 py-4 sm:py-3.5 rounded-xl text-[14px] hover:bg-white/15 transition-all backdrop-blur-sm min-h-[48px]">
                How It Works
              </Link>
            </div>
            <div className="flex items-center gap-6 sm:gap-8 mt-10 sm:mt-12 pt-8 border-t border-white/25">
              {[
                { label: 'Assets Under Custody', value: heroStats.custodyValue },
                { label: 'Verified Clients', value: heroStats.verifiedClients },
                { label: 'Years Operating', value: '12+' },
              ]?.map(stat => (
                <div key={stat?.label}>
                  <p className="text-xl sm:text-2xl font-bold text-white font-mono-nums">{stat?.value}</p>
                  <p className="text-[10px] sm:text-[11px] text-white/70 mt-0.5">{stat?.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* ── Feature Badges + Custody Services ────────────────────────────── */}
      <section className="bg-[#0d1b2a] py-14 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-white/10">
            {/* Feature badges — dark navy side */}
            <div className="pb-10 lg:pb-0 lg:pr-12">
              <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-3 xl:grid-cols-6 gap-4 sm:gap-6">
                {featureBadges?.map(badge => (
                  <div key={badge?.label} className="flex flex-col items-center gap-3 text-center">
                    <div className="text-accent w-10 h-10 flex items-center justify-center">
                      {badge?.icon}
                    </div>
                    <p className="text-[10px] font-700 text-white/80 uppercase tracking-wider leading-tight">{badge?.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Custody service icons — light side */}
            <div className="pt-10 lg:pt-0 lg:pl-12">
              <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-3 xl:grid-cols-6 gap-6">
                {custodyServices?.map(svc => (
                  <div key={svc?.label} className="flex flex-col items-center gap-3 text-center">
                    <div className="text-white/70 w-11 h-11 flex items-center justify-center">
                      {svc?.icon}
                    </div>
                    <p className="text-[10px] font-600 text-white/60 uppercase tracking-wider leading-tight">{svc?.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ── Services — asymmetric bento ───────────────────────────────────── */}
      <section id="services" className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-14">
            <p className="text-[11px] font-700 text-accent uppercase tracking-widest mb-3">What We Do</p>
            <h2 className="text-4xl font-bold text-primary mb-4">Everything you need to<br />protect what matters most</h2>
            <p className="text-[15px] text-muted-foreground max-w-[500px]">From gold bullion to rare collectibles, we handle custody, verification, and secure delivery — all under one roof.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="md:col-span-2 rounded-2xl overflow-hidden relative min-h-[280px] group">
              <Image
                src="/assets/images/16330-1784137616163.jpg"
                alt="Professional vault storage facility with secure asset management systems"
                fill
                className="object-cover object-center group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent" />
              <div className="absolute bottom-0 left-0 p-8">
                <span className="status-badge badge-gold text-[10px] mb-3 inline-block">Core Service</span>
                <h3 className="text-xl font-bold text-white mb-2">Physical Asset Custody</h3>
                <p className="text-[13px] text-white/75 leading-relaxed max-w-[380px]">Gold, diamonds, precious metals, jewellery, and documents stored in approved vault facilities with full chain-of-custody tracking.</p>
              </div>
            </div>

            <div className="card-elevated p-8 rounded-2xl flex flex-col justify-between">
              <div>
                <div className="w-11 h-11 rounded-xl bg-info-bg border border-info/20 flex items-center justify-center mb-5 text-blue-600">
                  <IconCashCustody />
                </div>
                <h3 className="text-[17px] font-bold text-primary mb-3">Cash Custody</h3>
                <p className="text-[13px] text-muted-foreground leading-relaxed">Secure cash deposit, counting, verification, and custody management with full transaction ledger and statement generation.</p>
              </div>
              <Link href="/services" className="mt-6 text-[12px] font-600 text-accent hover:underline inline-flex items-center gap-1">
                Learn more
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6h8M7 3l3 3-3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </Link>
            </div>

            <div className="card-elevated p-8 rounded-2xl flex flex-col justify-between">
              <div>
                <div className="w-11 h-11 rounded-xl bg-success-bg border border-success/20 flex items-center justify-center mb-5 text-green-600">
                  <IconSecureDelivery />
                </div>
                <h3 className="text-[17px] font-bold text-primary mb-3">Secure Delivery</h3>
                <p className="text-[13px] text-muted-foreground leading-relaxed">Controlled collection and delivery of assets with real-time status tracking, recipient verification, and proof-of-delivery documentation.</p>
              </div>
              <Link href="/services" className="mt-6 text-[12px] font-600 text-accent hover:underline inline-flex items-center gap-1">
                Learn more
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6h8M7 3l3 3-3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </Link>
            </div>

            <div className="md:col-span-2 card-elevated p-8 rounded-2xl border border-primary/10 bg-navy-light">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-5 text-primary">
                    <IconFullyCompliant />
                  </div>
                  <h3 className="text-[17px] font-bold text-primary mb-3">Asset Inspection & Verification</h3>
                  <p className="text-[13px] text-muted-foreground leading-relaxed">Professional inspection, authentication, weight verification, purity testing, and independent valuation for all accepted assets.</p>
                </div>
                <div>
                  <div className="w-11 h-11 rounded-xl bg-warning-bg border border-warning/20 flex items-center justify-center mb-5 text-amber-600">
                    <IconDocumentCustody />
                  </div>
                  <h3 className="text-[17px] font-bold text-primary mb-3">Private Client Accounts</h3>
                  <p className="text-[13px] text-muted-foreground leading-relaxed">Individual and corporate accounts with balance management, transaction history, custody certificates, and compliance reporting.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <Link href="/services" className="inline-flex items-center gap-2 text-[13px] font-600 text-primary border border-border rounded-xl px-6 py-3 hover:bg-muted transition-colors">
              View all services
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </Link>
          </div>
        </div>
      </section>
      {/* ── How Our Custody Process Works ────────────────────────────────── */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-white border-y border-border/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Process steps */}
            <div>
              <p className="text-[12px] font-700 text-primary uppercase tracking-widest mb-6 text-center">HOW OUR CUSTODY PROCESS WORKS</p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                {procesSteps?.map((step, i) => (
                  <React.Fragment key={step?.num}>
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-14 h-14 rounded-full bg-[#0d1b2a] flex items-center justify-center shadow-lg">
                        <span className="text-accent font-bold text-[15px]">{step?.num}</span>
                      </div>
                      <p className="text-[10px] font-600 text-[#0d1b2a] uppercase tracking-wide text-center max-w-[60px] leading-tight">{step?.label}</p>
                    </div>
                    {i < procesSteps?.length - 1 && (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="text-[#0d1b2a] mb-5 flex-shrink-0">
                        <path d="M4 10h12M12 6l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Brand lockup + security badges */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Brand card */}
              <div className="bg-[#0d1b2a] rounded-2xl p-8 flex flex-col items-center justify-center gap-4 min-h-[200px]">
                <Image
                  src="/assets/images/ChatGPT_Image_Jul_15__2026__06_13_59_PM-1784139267730.png"
                  alt="Kars Postasi Ltd gold shield crest"
                  width={80}
                  height={80}
                  className="object-contain"
                  style={{ mixBlendMode: 'screen' }}
                />
                <div className="text-center">
                  <p className="text-white font-bold text-[16px] tracking-wider">KARS POSTASI LTD</p>
                  <p className="text-accent/80 text-[10px] uppercase tracking-[0.15em] mt-1">Security | Privacy | Trust</p>
                </div>
              </div>

              {/* Security trust badges */}
              <div className="grid grid-cols-2 gap-4">
                {securityBadges?.map(badge => (
                  <div key={badge?.label} className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border/50 hover:border-accent/30 transition-colors">
                    <div className="text-[#0d1b2a]">
                      {badge?.icon}
                    </div>
                    <p className="text-[10px] font-700 text-[#0d1b2a] uppercase tracking-wide text-center leading-tight">{badge?.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ── Portal Navigation Preview ─────────────────────────────────────── */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-[#0d1b2a]">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] font-700 text-accent uppercase tracking-widest mb-6 sm:mb-8 text-center">Client Portal Navigation</p>
          <div className="grid grid-cols-5 sm:grid-cols-10 gap-3 sm:gap-4">
            {portalNav?.map(item => (
              <div key={item?.label} className="flex flex-col items-center gap-2 group cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center text-accent group-hover:text-white transition-colors">
                  {item?.icon}
                </div>
                <p className="text-[9px] font-600 text-white/60 uppercase tracking-wider text-center group-hover:text-white/90 transition-colors">{item?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ── About / Team ─────────────────────────────────────────────────── */}
      <section id="about" className="py-16 sm:py-24 px-4 sm:px-6 bg-muted/20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
              <Image
                src="/assets/images/2241-1784137616387.jpg"
                alt="KarsPostasi team members reviewing asset custody documentation in a professional setting"
                fill
                className="object-cover object-center"
              />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="bg-[#0d1b2a]/90 backdrop-blur-sm rounded-xl p-4 shadow-lg flex items-center gap-3">
                  <Image
                    src="/assets/images/ChatGPT_Image_Jul_15__2026__06_13_59_PM-1784139267730.png"
                    alt="Kars Postasi crest"
                    width={36}
                    height={36}
                    className="object-contain flex-shrink-0"
                    style={{ mixBlendMode: 'screen' }}
                  />
                  <div>
                    <p className="text-[13px] font-700 text-white">KarsPostasi Ltd</p>
                    <p className="text-[11px] text-accent/80">Registered in Scotland · SC756088</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <p className="text-[11px] font-700 text-accent uppercase tracking-widest mb-3">About Us</p>
              <h2 className="text-4xl font-bold text-primary mb-6">A trusted name in<br />secure asset custody</h2>
              <p className="text-[15px] text-muted-foreground leading-relaxed mb-5">
                KarsPostasi Ltd is a registered Scottish company providing secure digital infrastructure for the management, custody, deposit, withdrawal, storage, and delivery of money and valuable physical assets.
              </p>
              <p className="text-[15px] text-muted-foreground leading-relaxed mb-8">
                We work with high-net-worth individuals, businesses, gold traders, diamond dealers, security companies, and institutional clients who need a trusted environment for their most valuable assets.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-8">
                {[
                  { label: 'Registered Office', value: '272 Bath Street, Glasgow G2 4JR' },
                  { label: 'Jurisdiction', value: 'Scotland, United Kingdom' },
                  { label: 'Telephone', value: '+44 7887 533716' },
                  { label: 'Email', value: 'karspostasi@mail.com' },
                ]?.map(item => (
                  <div key={item?.label} className="p-4 rounded-xl bg-background border border-border/60">
                    <p className="text-[10px] font-700 text-muted-foreground uppercase tracking-wide mb-1">{item?.label}</p>
                    <p className="text-[12px] font-600 text-foreground">{item?.value}</p>
                  </div>
                ))}
              </div>
              <Link href="/about" className="inline-flex items-center gap-2 text-[13px] font-600 text-primary hover:text-accent transition-colors">
                Read our full story
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* ── Security ─────────────────────────────────────────────────────── */}
      <section id="security" className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-[11px] font-700 text-accent uppercase tracking-widest mb-3">Security & Compliance</p>
              <h2 className="text-4xl font-bold text-primary mb-6">Built for the highest<br />security standards</h2>
              <p className="text-[15px] text-muted-foreground leading-relaxed mb-10">Every transaction, every access, every movement is recorded in an immutable audit trail. No single employee can initiate, approve, and release the same high-value withdrawal.</p>
              <div className="space-y-3">
                {[
                  { title: 'Dual Authorization', desc: 'Maker-checker controls on all high-value releases — two separate officers required', Icon: IconAdvancedSecurity },
                  { title: 'Multi-Factor Authentication', desc: 'TOTP-based MFA enforced for all staff and high-value client accounts', Icon: IconSecureFacilities },
                  { title: 'Immutable Audit Logs', desc: 'Every action permanently recorded with officer ID, timestamp, and device fingerprint', Icon: IconFullyCompliant },
                  { title: 'Role-Based Access Control', desc: 'Granular permissions — vault locations visible only to authorized personnel', Icon: IconTrustedPartnership },
                ]?.map(item => (
                  <div key={item?.title} className="flex items-start gap-4 p-4 rounded-xl hover:bg-muted/50 transition-colors">
                    <div className="text-accent flex-shrink-0 mt-0.5 w-8 h-8">
                      <item.Icon />
                    </div>
                    <div>
                      <p className="text-[14px] font-700 text-foreground">{item?.title}</p>
                      <p className="text-[12px] text-muted-foreground mt-0.5 leading-relaxed">{item?.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="card-elevated p-8 rounded-2xl border border-primary/10 bg-navy-light">
              <p className="text-[12px] font-700 text-muted-foreground uppercase tracking-wide mb-6">Compliance Framework</p>
              <div className="space-y-3">
                {[
                  { label: 'KYC / AML Verification', status: 'Required', color: 'badge-success' },
                  { label: 'Source of Funds Declaration', status: 'Required', color: 'badge-success' },
                  { label: 'Sanctions Screening', status: 'Automated', color: 'badge-info' },
                  { label: 'PEP Screening', status: 'Automated', color: 'badge-info' },
                  { label: 'Transaction Monitoring', status: 'Real-time', color: 'badge-gold' },
                  { label: 'Regulatory Reporting', status: 'Supported', color: 'badge-navy' },
                  { label: 'Data Encryption (AES-256)', status: 'Active', color: 'badge-success' },
                  { label: 'GDPR Compliance', status: 'Active', color: 'badge-success' },
                ]?.map(item => (
                  <div key={item?.label} className="flex items-center justify-between py-2.5 border-b border-border/50 last:border-0">
                    <span className="text-[13px] text-foreground">{item?.label}</span>
                    <span className={`status-badge ${item?.color} text-[10px]`}>{item?.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 vault-gradient relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/3 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-60 h-60 rounded-full bg-accent/10 pointer-events-none" />
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="text-4xl font-bold text-white mb-5">Ready to secure your assets?</h2>
          <p className="text-[15px] text-white/70 leading-relaxed mb-10">
            Open a secure account today. Identity verification is required before activation. Our compliance team reviews all applications within 2 business days.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link href="/sign-up-login-screen" className="inline-flex items-center justify-center gap-2 bg-accent text-white font-700 px-8 py-4 rounded-xl text-[14px] shadow-lg hover:bg-accent/90 transition-all active:scale-[0.98] min-h-[48px]">
              Open Secure Account
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <Link href="/contact" className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-600 px-8 py-4 rounded-xl text-[14px] hover:bg-white/10 transition-all min-h-[48px]">
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer className="bg-primary text-white/60 py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-10">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <Image
                  src="/assets/images/ChatGPT_Image_Jul_15__2026__06_13_59_PM-1784139267730.png"
                  alt="Kars Postasi Ltd crest"
                  width={40}
                  height={40}
                  className="object-contain"
                  style={{ mixBlendMode: 'screen' }}
                />
                <span className="text-white font-bold text-[15px]">KARS POSTASI LTD</span>
              </div>
              <p className="text-[13px] leading-relaxed mb-4 max-w-[300px]">
                Secure private asset custody and vault management for individuals and institutions.
              </p>
              <p className="text-[12px] text-white/40">
                Registered in Scotland, Number SC756088
              </p>
            </div>
            <div>
              <p className="text-white/80 font-600 text-[13px] mb-4">Company</p>
              <div className="space-y-2.5">
                {[
                  { label: 'About Us', href: '/about' },
                  { label: 'Services', href: '/services' },
                  { label: 'How It Works', href: '/how-it-works' },
                  { label: 'Contact', href: '/contact' },
                ]?.map(item => (
                  <Link key={item?.href} href={item?.href} className="block text-[13px] hover:text-white/90 transition-colors">{item?.label}</Link>
                ))}
              </div>
            </div>
            <div>
              <p className="text-white/80 font-600 text-[13px] mb-4">Contact</p>
              <div className="space-y-2.5 text-[13px]">
                <p>272 Bath Street</p>
                <p>Glasgow G2 4JR</p>
                <p>Scotland, UK</p>
                <p className="pt-1">+44 7887 533716</p>
                <p>karspostasi@mail.com</p>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-[11px]">© 2026 KarsPostasi Ltd. All rights reserved.</p>
            <p className="text-[11px]">Registered in Scotland, Number SC756088 · 272 Bath Street, Glasgow G2 4JR</p>
          </div>
        </div>
      </footer>
    </div>
  );
}